import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { GreenText, LoadingContent, OrderableTableCell } from 'components'
import { getOhioOutgoingTitleVehicles } from 'controllers/ohioOutgoingTitles/index'
import { OhioOutgoingTitleVehicleDto } from 'dtos/ohioOutgoingTitles'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, formatDateMoment } from 'utils'

interface AddVehiclesToAGroupDialogProps {
  isLoading: boolean
  ohioOutgoingTitleGuid?: string
  onClose: () => void
  onSave: (values: OhioOutgoingTitleVehicleDto[]) => void
  open: boolean
}

export default function AddVehiclesToAGroupDialog({ isLoading, ohioOutgoingTitleGuid, onClose, onSave, open }: AddVehiclesToAGroupDialogProps) {
  const [isGettingVehicles, setIsGettingVehicles] = useState<boolean>(false)
  const [vehicles, setVehicles] = useState<OhioOutgoingTitleVehicleDto[]>([])
  const [selectedVehicles, setSelectedVehicles] = useState<OhioOutgoingTitleVehicleDto[]>([])

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OhioOutgoingTitleVehicleDto>('importerStockNumber')
  const [includeOhioOutgoingTitleVehiclesAlreadyInAGroup, setIncludeOhioOutgoingTitleVehiclesAlreadyInAGroup] = useState<boolean>(false)

  useEffect(() => {
    if (open && ohioOutgoingTitleGuid) {
      setIsGettingVehicles(true)
      getOhioOutgoingTitleVehicles(ohioOutgoingTitleGuid, includeOhioOutgoingTitleVehiclesAlreadyInAGroup)
        .then(res => {
          setVehicles(res.value)
        })
        .finally(() => {
          setIsGettingVehicles(false)
        })
    } else {
      setVehicles([])
      setSelectedVehicles([])
    }
  }, [ohioOutgoingTitleGuid, includeOhioOutgoingTitleVehiclesAlreadyInAGroup, open])

  const onChangeSortByDirection = (propertyName: keyof OhioOutgoingTitleVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <Dialog open={open} maxWidth='xl' fullWidth>
      <DialogContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item>
            <DialogTitle>Add Vehicles To A Group</DialogTitle>
          </Grid>

          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  checked={includeOhioOutgoingTitleVehiclesAlreadyInAGroup}
                  disabled={isLoading}
                  onChange={() => setIncludeOhioOutgoingTitleVehiclesAlreadyInAGroup(!includeOhioOutgoingTitleVehiclesAlreadyInAGroup)}
                />
              }
              label='Include Ohio Outgoing Title Vehicles Already In A Group'
            />
          </Grid>

          <LoadingContent isLoading={isGettingVehicles}>
            <Grid item xs={12}>
              <Card variant='outlined' sx={{ width: '100%' }}>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.length === vehicles.length && selectedVehicles.length !== 0}
                            disabled={isLoading || !vehicles.length}
                            indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== vehicles.length}
                            onChange={() => {
                              if (selectedVehicles.length === vehicles.length) {
                                setSelectedVehicles([])
                              } else if (vehicles) {
                                setSelectedVehicles(vehicles)
                              }
                            }}
                          />
                        </TableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='importerStockNumber'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Importer Stock #
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                          VIN
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='exporterCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Exporter
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='buyerCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Buyer
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='titleDestinationCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Title Destination
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                          Year
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                          Make
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='model'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Model
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='titleUploadedDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Title Uploaded
                        </OrderableTableCell>

                        <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                          keyName='signedOutboundBOLUploadedDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Signed BOL
                        </OrderableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles?.sort(clientSideOrderBy<OhioOutgoingTitleVehicleDto>(orderBy, order)).map((vehicle, index) => (
                        <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)}
                              disabled={isLoading}
                              onChange={() => {
                                if (selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)) {
                                  setSelectedVehicles(selectedVehicles.filter(selectedVehicle => selectedVehicle.vehicleGuid !== vehicle.vehicleGuid))
                                } else {
                                  setSelectedVehicles([...selectedVehicles, vehicle])
                                }
                              }}
                            />
                          </TableCell>

                          <TableCell>{vehicle.importerStockNumber}</TableCell>

                          <TableCell>{vehicle.vin}</TableCell>

                          <TableCell>{vehicle.exporterCompany ?? '-'}</TableCell>

                          <TableCell>{vehicle.buyerCompany ?? '-'}</TableCell>

                          <TableCell>{vehicle.titleDestinationCompany ?? '-'}</TableCell>

                          <TableCell>{vehicle.year}</TableCell>

                          <TableCell>{vehicle.make}</TableCell>

                          <TableCell>{vehicle.model}</TableCell>

                          <TableCell>{vehicle.titleUploadedDateTime ? formatDateMoment(vehicle.titleUploadedDateTime) : '-'}</TableCell>

                          <TableCell>{vehicle.signedOutboundBOLUploadedDateTime ? formatDateMoment(vehicle.signedOutboundBOLUploadedDateTime) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </LoadingContent>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Manage Vehicles:**

Vehicles appear in this list if they have a Ohio Title document associated with them but are not yet assigned to any Outgoing Titles Group.

Since a vehicle can have multiple Ohio Title documents and may require more than one title to be sent, the "Include Ohio Outgoing Title Vehicles Already in a Group" toggle is available.- When enabled, vehicles that already belong to at least one Outgoing Titles Group will also be displayed.

**Select Box:**

Clicking the checkbox selects the vehicle in that row.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
            </GreenText>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
          CANCEL
        </Button>

        <LoadingButton disabled={selectedVehicles.length === 0} loading={isLoading} onClick={() => onSave(selectedVehicles)} variant='contained'>
          SAVE
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
